import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export const useAuthStatus = () => {
  const [userRole, setUserRole] = useState<"talent" | "recruiter" | null>(null);
  const [isOpenToJobs, setIsOpenToJobs] = useState(true);
  const [isHiring, setIsHiring] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session check error:", sessionError);
          if (mounted) navigate('/login');
          return;
        }
        
        if (!session?.user) {
          console.log("No valid session found, redirecting to login");
          if (mounted) navigate('/login');
          return;
        }

        if (!mounted) return;

        // Fetch user role
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (roleError) {
          console.error('Error fetching user role:', roleError);
          return;
        }

        if (roleData && mounted) {
          setUserRole(roleData.role);
        }

        // Fetch profile data
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_open_to_jobs, is_hiring')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Error fetching profile data:', profileError);
          return;
        }

        if (profileData && mounted) {
          setIsOpenToJobs(profileData.is_open_to_jobs ?? true);
          setIsHiring(profileData.is_hiring ?? true);
        }
      } catch (error) {
        console.error('Error in checkSession:', error);
        if (mounted) navigate('/login');
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session);
      
      if (!mounted) return;
      
      if (event === 'SIGNED_OUT' || !session) {
        navigate('/login');
      } else if (event === 'SIGNED_IN' && session) {
        checkSession();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  return {
    userRole,
    isOpenToJobs,
    isHiring,
    setIsOpenToJobs,
    setIsHiring
  };
};